import { useState, useEffect } from 'react';
import AddressSearch from './AddressSearch';
import AddressMap from './AddressMap';
import CalendarModal from './CalendarModal';
import { API_BASE_URL, convertImageUrl } from './config';

interface PropertyRegistrationProps {
  onClose: () => void;
  onSuccess: () => void;
  propertyId?: number;
  token?: string;
}

type TabType = 'space' | 'rent' | 'options' | 'description' | 'schedule';

interface LongTermDiscount {
  weeks: number;
  rate: number;
}

interface ImmediateDiscount {
  days: number;
  amount: number;
}

interface FormData {
  // Space
  road_address: string;
  jibun_address: string;
  floor: string;
  detail_address: string;
  building_type: string;
  room_count: number;
  bathroom_count: number;
  kitchen_count: number;
  living_room_count: number;
  has_duplex: boolean;
  exclusive_area: number;
  has_elevator: boolean;
  parking_available: boolean;
  parking_option: string;
  parking_info: string;

  // Rent
  weekly_rent: number;
  deposit: number;
  long_term_discounts: LongTermDiscount[];
  immediate_discounts: ImmediateDiscount[];
  weekly_maintenance_fee: number;
  maintenance_includes_electricity: boolean;
  maintenance_includes_water: boolean;
  maintenance_includes_gas: boolean;
  maintenance_detail: string;
  checkout_cleaning_fee: number;
  cancellation_penalty: string;
  refund_policy_note: boolean;

  // Options
  basic_options: string[];
  additional_options: string[];
  pets_allowed: boolean;

  // Description
  room_name: string;
  room_introduction: string;
  usage_guide: string;
  transportation_info: string;
  additional_info: string;

  // Photos
  photo_urls: string[];

  // Schedule
  min_contract_weeks: number;
  available_from: string;
  available_until: string;
}

const PropertyRegistration = ({ onClose, onSuccess, propertyId, token }: PropertyRegistrationProps) => {
  const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const [activeTab, setActiveTab] = useState<TabType>('space');
  const [showAddressSearch, setShowAddressSearch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showBasicOptionsModal, setShowBasicOptionsModal] = useState<boolean>(false);
  const [showAdditionalOptionsModal, setShowAdditionalOptionsModal] = useState<boolean>(false);
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [tempBasicOptions, setTempBasicOptions] = useState<string[]>([]);
  const [tempAdditionalOptions, setTempAdditionalOptions] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>({
    road_address: '',
    jibun_address: '',
    floor: '',
    detail_address: '',
    building_type: '오피스텔',
    room_count: 0,
    bathroom_count: 0,
    kitchen_count: 0,
    living_room_count: 0,
    has_duplex: false,
    exclusive_area: 0,
    has_elevator: false,
    parking_available: false,
    parking_option: '무료',
    parking_info: '',
    weekly_rent: 0,
    deposit: 0,
    long_term_discounts: [],
    immediate_discounts: [],
    weekly_maintenance_fee: 0,
    maintenance_includes_electricity: false,
    maintenance_includes_water: false,
    maintenance_includes_gas: false,
    maintenance_detail: '',
    checkout_cleaning_fee: 0,
    cancellation_penalty: '보통',
    refund_policy_note: false,
    basic_options: [],
    additional_options: [],
    pets_allowed: false,
    room_name: '',
    room_introduction: '',
    usage_guide: '',
    transportation_info: '',
    additional_info: '',
    photo_urls: [],
    min_contract_weeks: 1,
    available_from: '',
    available_until: '',
  });

  const tabs = [
    { id: 'space' as TabType, label: '공간', number: 1 },
    { id: 'rent' as TabType, label: '임대료', number: 2 },
    { id: 'options' as TabType, label: '옵션', number: 3 },
    { id: 'description' as TabType, label: '방 설명', number: 4 },
    { id: 'schedule' as TabType, label: '일정', number: 5 },
  ];

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field: 'basic_options' | 'additional_options', value: string) => {
    setFormData(prev => {
      const currentArray = prev[field];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  const handleSelectAddress = (roadAddress: string, jibunAddress: string) => {
    setFormData(prev => ({
      ...prev,
      road_address: roadAddress,
      jibun_address: jibunAddress
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const uploadPromises = Array.from(files).map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${API_BASE_URL}/api/upload-image`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          return data.url;
        } else {
          console.error('Failed to upload image');
          return null;
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        return null;
      }
    });

    const uploadedUrls = await Promise.all(uploadPromises);
    const validUrls = uploadedUrls.filter(url => url !== null) as string[];

    if (validUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        photo_urls: [...prev.photo_urls, ...validUrls]
      }));
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photo_urls: prev.photo_urls.filter((_, i) => i !== index)
    }));
  };

  // 수정 모드일 때 기존 데이터 로드
  useEffect(() => {
    if (propertyId) {
      setLoading(true);
      fetch(`${API_BASE_URL}/api/properties/${propertyId}`, { headers: authHeaders })
        .then(response => response.json())
        .then(data => {
          setFormData({
            road_address: data.road_address || '',
            jibun_address: data.jibun_address || '',
            floor: data.floor || '',
            detail_address: data.detail_address || '',
            building_type: data.building_type || '',
            room_count: data.room_count || 0,
            bathroom_count: data.bathroom_count || 0,
            kitchen_count: data.kitchen_count || 0,
            living_room_count: data.living_room_count || 0,
            has_duplex: data.has_duplex || false,
            exclusive_area: data.exclusive_area || 0,
            has_elevator: data.has_elevator || false,
            parking_available: data.parking_available || false,
            parking_option: data.parking_option || '무료',
            parking_info: data.parking_info || '',
            weekly_rent: data.weekly_rent || 0,
            deposit: data.deposit || 0,
            long_term_discounts: data.long_term_discounts || [],
            immediate_discounts: data.immediate_discounts || [],
            weekly_maintenance_fee: data.weekly_maintenance_fee || 0,
            maintenance_includes_electricity: data.maintenance_includes_electricity || false,
            maintenance_includes_water: data.maintenance_includes_water || false,
            maintenance_includes_gas: data.maintenance_includes_gas || false,
            maintenance_detail: data.maintenance_detail || '',
            checkout_cleaning_fee: data.checkout_cleaning_fee || 0,
            cancellation_penalty: data.cancellation_penalty || '보통',
            refund_policy_note: data.refund_policy_note || false,
            basic_options: data.basic_options || [],
            additional_options: data.additional_options || [],
            pets_allowed: data.pets_allowed || false,
            room_name: data.room_name || '',
            room_introduction: data.room_introduction || '',
            usage_guide: data.usage_guide || '',
            transportation_info: data.transportation_info || '',
            additional_info: data.additional_info || '',
            photo_urls: data.photo_urls || [],
            min_contract_weeks: data.min_contract_weeks || 1,
            available_from: data.available_from || '',
            available_until: data.available_until || '',
          });
          setLoading(false);
        })
        .catch(error => {
          console.error('Failed to load property:', error);
          alert('매물 정보를 불러오는데 실패했습니다.');
          setLoading(false);
        });
    }
  }, [propertyId]);

  const handleSubmit = async () => {
    try {
      // 빈 문자열인 날짜 필드를 null로 변환
      const submitData = {
        ...formData,
        available_from: formData.available_from || null,
        available_until: formData.available_until || null,
      };

      const url = propertyId
        ? `${API_BASE_URL}/api/properties/${propertyId}`
        : `${API_BASE_URL}/api/properties`;

      const method = propertyId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        alert(propertyId ? '매물이 수정되었습니다!' : '매물이 등록되었습니다!');
        onSuccess();
        onClose();
      } else {
        const errorData = await response.json();
        console.error('Registration failed:', errorData);
        alert(propertyId ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert(propertyId ? '수정 중 오류가 발생했습니다.' : '등록 중 오류가 발생했습니다.');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'space':
        return (
          <div style={{ padding: '20px' }}>
            {/* 경고 메시지 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              marginBottom: '20px',
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#000',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                !
              </div>
              <span style={{ fontSize: '14px', color: '#333' }}>
                주소는 수정이 불가하니 정확히 입력해 주세요.
              </span>
            </div>

            {/* 주소 */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <label style={{ fontWeight: 'bold', fontSize: '16px' }}>주소</label>
                <button
                  type="button"
                  onClick={() => setShowAddressSearch(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#7c3aed',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  수정하기
                </button>
              </div>

              {formData.road_address ? (
                <div style={{
                  padding: '15px',
                  backgroundColor: '#f8f8f8',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#666', marginRight: '10px' }}>도로명</span>
                    <span style={{ fontSize: '14px', color: '#333' }}>{formData.road_address}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '13px', color: '#666', marginRight: '10px' }}>지번</span>
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      {formData.jibun_address || '-'}
                    </span>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setShowAddressSearch(true)}
                  style={{
                    padding: '15px',
                    backgroundColor: '#f8f8f8',
                    borderRadius: '8px',
                    border: '1px dashed #ccc',
                    textAlign: 'center',
                    color: '#999',
                    cursor: 'pointer'
                  }}
                >
                  주소를 검색하려면 클릭하세요
                </div>
              )}
            </div>

            {/* 지도 */}
            {formData.road_address && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '16px' }}>
                  위치
                </label>
                <AddressMap address={formData.road_address} />
              </div>
            )}

            {/* 층수 */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '16px' }}>
                층수
              </label>
              <select
                value={formData.floor}
                onChange={(e) => handleInputChange('floor', e.target.value)}
                style={inputStyle}
              >
                <option value="">선택</option>
                {Array.from({ length: 50 }, (_, i) => (
                  <option key={i + 1} value={`${i + 1}층`}>{i + 1}층</option>
                ))}
              </select>
            </div>

            {/* 상세 주소 */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '16px' }}>
                상세 주소
              </label>
              <div style={{
                fontSize: '13px',
                color: '#666',
                marginBottom: '10px'
              }}>
                상세 주소는 게시어 확정될 후 공개됩니다.
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="예: 101호"
                  value={formData.detail_address}
                  onChange={(e) => handleInputChange('detail_address', e.target.value)}
                  style={{
                    ...inputStyle,
                    paddingRight: '40px'
                  }}
                />
                {formData.detail_address && (
                  <button
                    type="button"
                    onClick={() => handleInputChange('detail_address', '')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: '#ccc',
                      color: '#fff',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>건물 유형</label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '10px',
                marginTop: '10px'
              }}>
                {['오피스텔', '아파트', '원룸/집투', '연립빌라', '단독주택', '상가주택',
                  '게스트하우스', '호텔', '모텔/여관', '펜션', '고시원', '쉐어하우스'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleInputChange('building_type', type)}
                    style={{
                      padding: '12px',
                      border: formData.building_type === type ? 'none' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: formData.building_type === type ? '#000' : '#fff',
                      color: formData.building_type === type ? '#fff' : '#333',
                      fontSize: '14px',
                      fontWeight: formData.building_type === type ? 'bold' : 'normal',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (formData.building_type !== type) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.building_type !== type) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>공간 구조</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                {([
                  { label: '방', field: 'room_count' as const },
                  { label: '화장실', field: 'bathroom_count' as const },
                  { label: '주방', field: 'kitchen_count' as const },
                  { label: '거실', field: 'living_room_count' as const }
                ] as const).map(({ label, field }) => (
                  <div key={field} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px'
                  }}>
                    <label style={{ fontSize: '14px', minWidth: '60px' }}>{label}</label>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: 1,
                      justifyContent: 'flex-end'
                    }}>
                      <button
                        type="button"
                        onClick={() => {
                          const currentValue = formData[field] as number;
                          if (currentValue > 0) {
                            handleInputChange(field, currentValue - 1);
                          }
                        }}
                        style={{
                          width: '40px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          fontSize: '18px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        −
                      </button>
                      <div style={{
                        width: '60px',
                        height: '40px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: '#f8f8f8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px'
                      }}>
                        {formData[field] as number}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const currentValue = formData[field] as number;
                          handleInputChange(field, currentValue + 1);
                        }}
                        style={{
                          width: '40px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          fontSize: '18px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.has_duplex}
                    onChange={(e) => handleInputChange('has_duplex', e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px' }}>복층이 있습니다.</span>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>전용면적 (평)</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  step="0.01"
                  value={formData.exclusive_area || ''}
                  onChange={(e) => handleInputChange('exclusive_area', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  style={{
                    ...inputStyle,
                    paddingRight: '80px',
                    MozAppearance: 'textfield'
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="no-spinner"
                />
                {formData.exclusive_area > 0 && (
                  <button
                    type="button"
                    onClick={() => handleInputChange('exclusive_area', 0)}
                    style={{
                      position: 'absolute',
                      right: '50px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: '#ccc',
                      color: '#fff',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0
                    }}
                  >
                    ×
                  </button>
                )}
                <span style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '14px',
                  color: '#666'
                }}>
                  평
                </span>
              </div>
              <div style={{
                marginTop: '8px',
                padding: '10px',
                backgroundColor: '#f8f8f8',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#666'
              }}>
                안내<br />
                • 공급 면적이 아닌 전용 면적 기준으로 입력해 주세요.
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>공간 추가 정보</h4>

              {/* 엘리베이터 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: 'bold' }}>
                  엘리베이터
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => handleInputChange('has_elevator', true)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: formData.has_elevator ? '2px solid #000' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: formData.has_elevator ? '#f8f8f8' : '#fff',
                      fontSize: '14px',
                      fontWeight: formData.has_elevator ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    있음
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('has_elevator', false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: !formData.has_elevator ? '2px solid #000' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: !formData.has_elevator ? '#f8f8f8' : '#fff',
                      fontSize: '14px',
                      fontWeight: !formData.has_elevator ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    없음
                  </button>
                </div>
              </div>

              {/* 주차 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: 'bold' }}>
                  주차
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => handleInputChange('parking_available', true)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: formData.parking_available ? '2px solid #000' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: formData.parking_available ? '#f8f8f8' : '#fff',
                      fontSize: '14px',
                      fontWeight: formData.parking_available ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    가능
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('parking_available', false);
                      handleInputChange('parking_option', '');
                      handleInputChange('parking_info', '');
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: !formData.parking_available ? '2px solid #000' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: !formData.parking_available ? '#f8f8f8' : '#fff',
                      fontSize: '14px',
                      fontWeight: !formData.parking_available ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    불가능
                  </button>
                </div>
              </div>

              {/* 주차 옵션 (주차 가능일 때만 표시) */}
              {formData.parking_available && (
                <>
                  <div style={{ marginBottom: '20px', paddingLeft: '10px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        cursor: 'pointer',
                        padding: '8px 0'
                      }}>
                        <input
                          type="radio"
                          name="parking_option"
                          checked={formData.parking_option === '무료(여유)'}
                          onChange={() => handleInputChange('parking_option', '무료(여유)')}
                          style={{ marginTop: '2px' }}
                        />
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>무료(여유)</div>
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            여유로운 무료 주차 공간이 있습니다.
                          </div>
                        </div>
                      </label>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        cursor: 'pointer',
                        padding: '8px 0'
                      }}>
                        <input
                          type="radio"
                          name="parking_option"
                          checked={formData.parking_option === '무료(선착순)'}
                          onChange={() => handleInputChange('parking_option', '무료(선착순)')}
                          style={{ marginTop: '2px' }}
                        />
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>무료(선착순)</div>
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            선착순으로 이용 가능한 무료 주차 공간이 있습니다.
                          </div>
                        </div>
                      </label>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        cursor: 'pointer',
                        padding: '8px 0'
                      }}>
                        <input
                          type="radio"
                          name="parking_option"
                          checked={formData.parking_option === '유료'}
                          onChange={() => handleInputChange('parking_option', '유료')}
                          style={{ marginTop: '2px' }}
                        />
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>유료</div>
                          <div style={{ fontSize: '13px', color: '#666' }}>
                            유료 주차 공간이 있습니다.
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* 주차 안내 */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}>
                      주차 안내 <span style={{ color: '#999', fontWeight: 'normal', fontSize: '12px' }}>(선택)</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <textarea
                        value={formData.parking_info || ''}
                        onChange={(e) => {
                          if (e.target.value.length <= 100) {
                            handleInputChange('parking_info', e.target.value);
                          }
                        }}
                        placeholder="주차와 관련된 안내 사항을 입력해 주세요"
                        style={{
                          ...inputStyle,
                          minHeight: '100px',
                          resize: 'vertical',
                          paddingBottom: '30px'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        fontSize: '12px',
                        color: '#999'
                      }}>
                        {(formData.parking_info || '').length} / 100
                      </div>
                    </div>
                    <div style={{
                      marginTop: '8px',
                      padding: '12px',
                      backgroundColor: '#f8f8f8',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#666'
                    }}>
                      예시<br />
                      • 1일 주차요금은 15,000원입니다.<br />
                      • 최대 2대 주차 가능하며, 사전 등록이 필요합니다.
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 'rent':
        return (
          <div style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '20px' }}>임대료 정보</h3>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>1주 임대료</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="text"
                  value={formData.weekly_rent.toLocaleString('ko-KR')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '');
                    if (/^\d*$/.test(value)) {
                      handleInputChange('weekly_rent', parseInt(value) || 0);
                    }
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <span style={{ fontSize: '14px', color: '#666' }}>원</span>
                <span style={{
                  fontSize: '14px',
                  color: '#666',
                  backgroundColor: '#f0f0f0',
                  padding: '5px 10px',
                  borderRadius: '4px'
                }}>1주</span>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>보증금</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="text"
                  value={formData.deposit.toLocaleString('ko-KR')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '');
                    if (/^\d*$/.test(value)) {
                      handleInputChange('deposit', parseInt(value) || 0);
                    }
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <span style={{ fontSize: '14px', color: '#666' }}>원</span>
              </div>
            </div>

            {/* 장기 계약 할인 */}
            <div style={{ marginTop: '30px', marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px' }}>장기 계약 할인</h4>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                장기간 가족하는 게스트에게 제공되는 할인입니다.
              </p>

              {/* 예시 */}
              <div style={{
                marginBottom: '15px',
                padding: '10px',
                backgroundColor: '#f8f8f8',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#666'
              }}>
                예시 · 2주 이상 계약 시, 5% 할인 제공
              </div>

              {formData.long_term_discounts.map((discount, index) => (
                <div key={index} style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '10px',
                  alignItems: 'center'
                }}>
                  <select
                    value={discount.weeks}
                    onChange={(e) => {
                      const newDiscounts = [...formData.long_term_discounts];
                      newDiscounts[index].weeks = parseInt(e.target.value);
                      handleInputChange('long_term_discounts', newDiscounts);
                    }}
                    style={{ ...inputStyle, flex: '0 0 80px' }}
                  >
                    <option value={0}>0주</option>
                    <option value={2}>2주</option>
                    <option value={3}>3주</option>
                    <option value={4}>4주</option>
                    <option value={8}>8주</option>
                    <option value={12}>12주</option>
                  </select>

                  <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>이상 계약 시</span>

                  <input
                    type="number"
                    value={discount.rate}
                    onChange={(e) => {
                      const newDiscounts = [...formData.long_term_discounts];
                      newDiscounts[index].rate = parseFloat(e.target.value) || 0;
                      handleInputChange('long_term_discounts', newDiscounts);
                    }}
                    style={{ ...inputStyle, flex: '0 0 80px' }}
                    min="0"
                    max="100"
                  />

                  <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>% 할인</span>

                  <button
                    type="button"
                    onClick={() => {
                      const newDiscounts = formData.long_term_discounts.filter((_, i) => i !== index);
                      handleInputChange('long_term_discounts', newDiscounts);
                    }}
                    style={{
                      padding: '8px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '18px',
                      color: '#999',
                      marginLeft: 'auto'
                    }}
                  >
                    ⓧ
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  handleInputChange('long_term_discounts', [
                    ...formData.long_term_discounts,
                    { weeks: 2, rate: 5 }
                  ]);
                }}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#000',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  width: '100%'
                }}
              >
                + 장기 계약 할인 추가
              </button>
            </div>

            {/* 즉시 입주 할인 */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px' }}>즉시 입주 할인</h4>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                입주일에 입주하는 게스트에게 제공하는 할인입니다.
              </p>

              {/* 예시 */}
              <div style={{
                marginBottom: '15px',
                padding: '10px',
                backgroundColor: '#f8f8f8',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#666'
              }}>
                예시 · 오늘부터 1일 이내 입주 시, 3만 원 할인 제공
              </div>

              {formData.immediate_discounts.map((discount, index) => (
                <div key={index} style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '10px',
                  alignItems: 'center'
                }}>
                  <input
                    type="number"
                    value={discount.days}
                    onChange={(e) => {
                      const newDiscounts = [...formData.immediate_discounts];
                      newDiscounts[index].days = parseInt(e.target.value) || 0;
                      handleInputChange('immediate_discounts', newDiscounts);
                    }}
                    style={{ ...inputStyle, flex: '0 0 80px' }}
                    min="0"
                    max="7"
                  />

                  <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>일 이내 입주 시</span>

                  <input
                    type="number"
                    value={discount.amount}
                    onChange={(e) => {
                      const newDiscounts = [...formData.immediate_discounts];
                      newDiscounts[index].amount = parseInt(e.target.value) || 0;
                      handleInputChange('immediate_discounts', newDiscounts);
                    }}
                    style={{ ...inputStyle, flex: '0 0 100px' }}
                    min="0"
                  />

                  <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>만 원 할인</span>

                  <button
                    type="button"
                    onClick={() => {
                      const newDiscounts = formData.immediate_discounts.filter((_, i) => i !== index);
                      handleInputChange('immediate_discounts', newDiscounts);
                    }}
                    style={{
                      padding: '8px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '18px',
                      color: '#999',
                      marginLeft: 'auto'
                    }}
                  >
                    ⓧ
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  handleInputChange('immediate_discounts', [
                    ...formData.immediate_discounts,
                    { days: 2, amount: 2 }
                  ]);
                }}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#000',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  width: '100%'
                }}
              >
                + 즉시 입주 할인 추가
              </button>
            </div>

            <h4 style={{ marginTop: '30px', marginBottom: '15px' }}>관리비 및 청소비</h4>

            {/* 1주 관리비 */}
            <div style={{ marginBottom: '25px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '15px' }}>1주 관리비</label>
                <a href="#" style={{ fontSize: '13px', color: '#666', textDecoration: 'underline' }}>관리비 알아보기</a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="text"
                  value={formData.weekly_maintenance_fee.toLocaleString('ko-KR')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '');
                    if (/^\d*$/.test(value)) {
                      handleInputChange('weekly_maintenance_fee', parseInt(value) || 0);
                    }
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="0"
                />
                <span style={{ fontWeight: 'bold', minWidth: '30px' }}>원</span>
                <span style={{ color: '#999', minWidth: '50px' }}>/ 1주</span>
              </div>
            </div>

            {/* 관리비 포함 항목 */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', fontSize: '15px' }}>관리비 포함 항목</label>
              <div style={{ display: 'flex', gap: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div
                    onClick={() => handleInputChange('maintenance_includes_electricity', !formData.maintenance_includes_electricity)}
                    style={{
                      width: '44px',
                      height: '24px',
                      backgroundColor: formData.maintenance_includes_electricity ? '#000' : '#ccc',
                      borderRadius: '12px',
                      position: 'relative',
                      transition: 'background-color 0.2s',
                      cursor: 'pointer'
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        position: 'absolute',
                        top: '2px',
                        left: formData.maintenance_includes_electricity ? '22px' : '2px',
                        transition: 'left 0.2s'
                      }}
                    />
                  </div>
                  <span>전기</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div
                    onClick={() => handleInputChange('maintenance_includes_water', !formData.maintenance_includes_water)}
                    style={{
                      width: '44px',
                      height: '24px',
                      backgroundColor: formData.maintenance_includes_water ? '#000' : '#ccc',
                      borderRadius: '12px',
                      position: 'relative',
                      transition: 'background-color 0.2s',
                      cursor: 'pointer'
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        position: 'absolute',
                        top: '2px',
                        left: formData.maintenance_includes_water ? '22px' : '2px',
                        transition: 'left 0.2s'
                      }}
                    />
                  </div>
                  <span>수도</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div
                    onClick={() => handleInputChange('maintenance_includes_gas', !formData.maintenance_includes_gas)}
                    style={{
                      width: '44px',
                      height: '24px',
                      backgroundColor: formData.maintenance_includes_gas ? '#000' : '#ccc',
                      borderRadius: '12px',
                      position: 'relative',
                      transition: 'background-color 0.2s',
                      cursor: 'pointer'
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        position: 'absolute',
                        top: '2px',
                        left: formData.maintenance_includes_gas ? '22px' : '2px',
                        transition: 'left 0.2s'
                      }}
                    />
                  </div>
                  <span>가스</span>
                </label>
              </div>
            </div>

            {/* 관리비 상세 설명 */}
            <div style={{ marginBottom: '25px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '15px' }}>관리비 상세 설명</label>
                <a href="#" style={{ fontSize: '13px', color: '#666', textDecoration: 'underline' }}>자주 묻는 문구 불러오기</a>
              </div>
              <textarea
                value={formData.maintenance_detail}
                onChange={(e) => {
                  if (e.target.value.length <= 300) {
                    handleInputChange('maintenance_detail', e.target.value);
                  }
                }}
                style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                placeholder="관리비에 포함된 항목, 별도 부담 항목 등을 상세히 작성해주세요."
              />
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#999', marginTop: '5px' }}>
                {formData.maintenance_detail.length}/300
              </div>
            </div>

            {/* 퇴실 청소비 */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', fontSize: '15px' }}>퇴실 청소비</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={formData.checkout_cleaning_fee.toLocaleString('ko-KR')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '');
                    if (/^\d*$/.test(value)) {
                      handleInputChange('checkout_cleaning_fee', parseInt(value) || 0);
                    }
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="0"
                />
                <span style={{ fontWeight: 'bold', minWidth: '30px' }}>원</span>
              </div>
              <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.6' }}>
                <p style={{ margin: '5px 0' }}>• 청소비는 보증금에서 차감됩니다.</p>
                <p style={{ margin: '5px 0' }}>• 청소 업체 이용 시 실비를 안내해주세요.</p>
                <p style={{ margin: '5px 0' }}>• 청소비를 받지 않는다면 0원으로 입력해주세요.</p>
              </div>
            </div>

            {/* 계약 취소 시 위약금 */}
            <div style={{ marginTop: '30px', marginBottom: '25px' }}>
              <h4 style={{ marginBottom: '15px' }}>계약 취소 시 위약금</h4>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
                설정하신 환불 규정에 따라 계약 취소 위약금이 부과됩니다.
              </p>

              {/* 라디오 버튼 */}
              <div style={{ display: 'flex', gap: '30px', marginBottom: '20px' }}>
                {['약한', '보통', '강한'].map(option => (
                  <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <div
                      onClick={() => handleInputChange('cancellation_penalty', option)}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: '2px solid ' + (formData.cancellation_penalty === option ? '#000' : '#ccc'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      {formData.cancellation_penalty === option && (
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: '#000'
                          }}
                        />
                      )}
                    </div>
                    <span style={{ fontWeight: formData.cancellation_penalty === option ? 'bold' : 'normal' }}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>

              {/* 위약금 표 */}
              <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f8f8' }}>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd', fontWeight: 'bold', fontSize: '14px' }}>위약금</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #ddd',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        backgroundColor: formData.cancellation_penalty === '약한' ? '#F0EDFF' : '#f8f8f8'
                      }}>약한</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #ddd',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        backgroundColor: formData.cancellation_penalty === '보통' ? '#F0EDFF' : '#f8f8f8'
                      }}>보통</th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #ddd',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        backgroundColor: formData.cancellation_penalty === '강한' ? '#F0EDFF' : '#f8f8f8'
                      }}>강한</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #ddd', fontSize: '13px' }}>입주일 15일 이전</td>
                      <td style={{
                        padding: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '약한' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '약한' ? 'bold' : 'normal'
                      }}>무료 취소</td>
                      <td style={{
                        padding: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '보통' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '보통' ? 'bold' : 'normal'
                      }}>입대료의 10%</td>
                      <td style={{
                        padding: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '강한' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '강한' ? 'bold' : 'normal'
                      }}>입대료의 20%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #ddd', fontSize: '13px' }}>입주일 8일 이전</td>
                      <td style={{
                        padding: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '약한' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '약한' ? 'bold' : 'normal'
                      }}>입대료의 20%</td>
                      <td style={{
                        padding: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '보통' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '보통' ? 'bold' : 'normal'
                      }}>입대료의 30%</td>
                      <td style={{
                        padding: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '강한' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '강한' ? 'bold' : 'normal'
                      }}>입대료의 50%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid #ddd', fontSize: '13px' }}>입주일 1일 이전</td>
                      <td style={{
                        padding: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '약한' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '약한' ? 'bold' : 'normal'
                      }}>입대료의 40%</td>
                      <td style={{
                        padding: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '보통' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '보통' ? 'bold' : 'normal'
                      }}>입대료의 50%</td>
                      <td style={{
                        padding: '12px',
                        borderBottom: '1px solid #ddd',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '강한' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '강한' ? 'bold' : 'normal'
                      }}>입대료의 70%</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', fontSize: '13px' }}>입주일 당일 이후</td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '약한' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '약한' ? 'bold' : 'normal'
                      }}>취소 불가</td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '보통' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '보통' ? 'bold' : 'normal'
                      }}>취소 불가</td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: formData.cancellation_penalty === '강한' ? '#F0EDFF' : 'white',
                        fontWeight: formData.cancellation_penalty === '강한' ? 'bold' : 'normal'
                      }}>취소 불가</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 안내 */}
              <div style={{ padding: '15px', backgroundColor: '#F7F8FA', borderRadius: '8px', marginBottom: '20px' }}>
                <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#6B4EFF' }}>안내</h5>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#666', lineHeight: '1.8' }}>
                  <li>결제 당일 취소 시, 환불 규정과 관계 없이 입대료의 10%만 위약금으로 부과됩니다.<br />단, 무료 취소 기간에 해당하는 경우 전액 환불됩니다.</li>
                  <li>관리비, 청소비, 보증금은 전액 환불됩니다.</li>
                </ul>
              </div>

              {/* 환불 규정 주의사항 */}
              <div style={{
                padding: '15px',
                border: '1px solid #FFE5E5',
                borderRadius: '8px',
                backgroundColor: '#FFF9F9'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ color: '#FF4444', fontWeight: 'bold', fontSize: '16px' }}>⚠</span>
                  <h5 style={{ margin: 0, fontSize: '14px', color: '#FF4444', fontWeight: 'bold' }}>환불 규정 주의사항</h5>
                </div>
                <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666', lineHeight: '1.6' }}>
                  • 호스트가 계약을 취소하는 경우에도 선정한 규정으로 계스트에게 위약금을 지급해야 합니다.
                </p>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.refund_policy_note}
                    onChange={(e) => handleInputChange('refund_policy_note', e.target.checked)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px', fontWeight: 'bold' }}>환불 규정 및 주의사항을 확인하였습니다.</span>
                </label>
              </div>
            </div>
          </div>
        );

      case 'options':
        return (
          <div style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '30px', fontSize: '20px', fontWeight: 'bold' }}>옵션을<br />선택해 주세요</h3>

            {/* 기본 옵션 */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '16px' }}>기본 옵션</label>
                <span style={{ fontSize: '13px', color: '#666' }}>{formData.basic_options.length}개 선택</span>
              </div>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                냉장고, 세탁기 등 생활에 포함된 기록 옵션입니다.
              </p>

              {/* 선택된 옵션 표시 */}
              {formData.basic_options.length > 0 && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f8f8f8',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  {formData.basic_options.join(', ')}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setTempBasicOptions([...formData.basic_options]);
                  setShowBasicOptionsModal(true);
                }}
                style={{
                  width: '100%',
                  padding: '15px',
                  backgroundColor: '#000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                기본 옵션 선택하기 →
              </button>
            </div>

            {/* 추가 옵션 */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '16px' }}>추가 옵션</label>
                <span style={{ fontSize: '13px', color: '#666' }}>{formData.additional_options.length}개 선택</span>
              </div>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                소화 가전, 편의 시설 등 생활에 포함된 추가 옵션입니다.
              </p>

              {/* 선택된 옵션 표시 */}
              {formData.additional_options.length > 0 && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f8f8f8',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  {formData.additional_options.join(', ')}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setTempAdditionalOptions([...formData.additional_options]);
                  setShowAdditionalOptionsModal(true);
                }}
                style={{
                  width: '100%',
                  padding: '15px',
                  backgroundColor: '#000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                추가 옵션 선택하기 →
              </button>
            </div>

            {/* 반려동물 동반 */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold', fontSize: '16px' }}>반려동물 동반</label>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                하늘을 선택하면 반려동물을 동반 가능 방으로 표시됩니다.
              </p>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => handleInputChange('pets_allowed', true)}
                  style={{
                    flex: 1,
                    padding: '15px',
                    backgroundColor: formData.pets_allowed ? '#000' : 'white',
                    color: formData.pets_allowed ? 'white' : '#333',
                    border: formData.pets_allowed ? 'none' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  허용
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('pets_allowed', false)}
                  style={{
                    flex: 1,
                    padding: '15px',
                    backgroundColor: !formData.pets_allowed ? '#000' : 'white',
                    color: !formData.pets_allowed ? 'white' : '#333',
                    border: !formData.pets_allowed ? 'none' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  허용 안 함
                </button>
              </div>
            </div>
          </div>
        );

      case 'description':
        return (
          <div style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '20px' }}>방 설명</h3>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>방 이름</label>
              <input
                type="text"
                value={formData.room_name}
                onChange={(e) => handleInputChange('room_name', e.target.value)}
                style={inputStyle}
                placeholder="예: 강남역 도보 5분 깔끔한 투룸"
              />
            </div>

            {/* 방 사진 등록 */}
            <div style={{ marginBottom: '30px' }}>
              <h4 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 'bold' }}>방 사진 등록 {formData.photo_urls.length}/20</h4>

              {/* 이미지 그리드 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '15px',
                marginBottom: '20px'
              }}>
                {formData.photo_urls.map((url, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      width: '100%',
                      paddingBottom: '100%',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    {/* 이미지 */}
                    <img
                      src={convertImageUrl(url)}
                      alt={`방 사진 ${index + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />

                    {/* 대표 사진 라벨 */}
                    {index === 0 && (
                      <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '8px',
                        backgroundColor: '#6B4EFF',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        대표 사진
                      </div>
                    )}

                    {/* 삭제 버튼 */}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: '#000',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        lineHeight: '1'
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              {/* 사진 첨부 버튼 */}
              <label style={{
                display: 'block',
                width: '100%',
                padding: '15px',
                backgroundColor: '#000',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 'bold',
                cursor: 'pointer',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                + 사진 첨부
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </label>

              {/* 안내 문구 */}
              <div style={{
                padding: '15px',
                backgroundColor: '#f8f8f8',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#666',
                lineHeight: '1.8'
              }}>
                <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold', color: '#333' }}>안내</h5>
                <p style={{ margin: '5px 0' }}>• 사진 등록 시 사진 확장 팁 확인부탁드립니다.</p>
                <p style={{ margin: '5px 0' }}>• 방 사진은 최소 6장 ~ 최대 20장까지 등록이 가능합니다.</p>
                <p style={{ margin: '5px 0' }}>• 사진을 길게 눌러 이동시키면 사진 순서를 변경할 수 있습니다.</p>
              </div>
            </div>

            {/* 방 소개 */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold', fontSize: '16px' }}>방 소개</label>

              <div style={{
                backgroundColor: '#f8f8f8',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
                fontSize: '13px',
                color: '#666',
                lineHeight: '1.6'
              }}>
                <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>예시</div>
                <p style={{ margin: '5px 0' }}>• 최근 리모델링 완료한 깔끔한 방입니다.</p>
                <p style={{ margin: '5px 0' }}>• 침대와 TV까지 모두 것은 풀옵션 방입니다.</p>
              </div>

              <textarea
                value={formData.room_introduction}
                onChange={(e) => {
                  if (e.target.value.length <= 300) {
                    handleInputChange('room_introduction', e.target.value);
                  }
                }}
                style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                placeholder="방에 대한 전반적인 소개를 작성해주세요"
              />
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#999', marginTop: '5px' }}>
                {formData.room_introduction.length}/300
              </div>
            </div>

            {/* 방 이용 방법 */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold', fontSize: '16px' }}>
                방 이용 방법 <span style={{ fontSize: '13px', color: '#999', fontWeight: 'normal' }}>(선택)</span>
              </label>

              <div style={{
                backgroundColor: '#f8f8f8',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
                fontSize: '13px',
                color: '#666',
                lineHeight: '1.6'
              }}>
                <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>예시</div>
                <p style={{ margin: '5px 0' }}>• 게스트 환영하면 이용 안내 메시지를 보냅니다.</p>
                <p style={{ margin: '5px 0' }}>• 실내 흡연이 불가한 방입니다.</p>
              </div>

              <textarea
                value={formData.usage_guide}
                onChange={(e) => {
                  if (e.target.value.length <= 700) {
                    handleInputChange('usage_guide', e.target.value);
                  }
                }}
                style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                placeholder="체크인/체크아웃 시간, 키 수령 방법 등"
              />
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#999', marginTop: '5px' }}>
                {formData.usage_guide.length}/700
              </div>
            </div>

            {/* 교통편 */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold', fontSize: '16px' }}>교통편</label>

              <div style={{
                backgroundColor: '#f8f8f8',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
                fontSize: '13px',
                color: '#666',
                lineHeight: '1.6'
              }}>
                <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>예시</div>
                <p style={{ margin: '5px 0' }}>• 신촌역 3분 거리입니다.</p>
                <p style={{ margin: '5px 0' }}>• 집 앞 버스정류장에서 강남으로 가는 버스를 타실 수 있습니다.</p>
              </div>

              <textarea
                value={formData.transportation_info}
                onChange={(e) => {
                  if (e.target.value.length <= 200) {
                    handleInputChange('transportation_info', e.target.value);
                  }
                }}
                style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                placeholder="지하철역, 버스 정류장 등 교통 정보"
              />
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#999', marginTop: '5px' }}>
                {formData.transportation_info.length}/200
              </div>
            </div>

            {/* 추가 설명 */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold', fontSize: '16px' }}>
                추가 설명 <span style={{ fontSize: '13px', color: '#999', fontWeight: 'normal' }}>(선택)</span>
              </label>

              <div style={{
                backgroundColor: '#f8f8f8',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '15px',
                fontSize: '13px',
                color: '#666',
                lineHeight: '1.6'
              }}>
                <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>예시</div>
                <p style={{ margin: '5px 0' }}>• 가까운 곳에 산책하기 좋은 공원이 있습니다.</p>
                <p style={{ margin: '5px 0' }}>• 주변에 마트, 병원, 맛집, 카페가 많아 생활하기 편리합니다.</p>
              </div>

              <textarea
                value={formData.additional_info}
                onChange={(e) => {
                  if (e.target.value.length <= 700) {
                    handleInputChange('additional_info', e.target.value);
                  }
                }}
                style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                placeholder="주변 편의시설, 관광지 등 내 방의 특별한 매력을 더 소개해 주세요"
              />
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#999', marginTop: '5px' }}>
                {formData.additional_info.length}/700
              </div>
            </div>
          </div>
        );

      case 'schedule':
        return (
          <div style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '20px' }}>일정 정보</h3>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>최소 계약 기간</label>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px', marginTop: '5px' }}>
                설정하신 최소 계약 기간 미만으로는 계약이 되지 않습니다.
              </p>
              <select
                value={formData.min_contract_weeks}
                onChange={(e) => handleInputChange('min_contract_weeks', parseInt(e.target.value))}
                style={inputStyle}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(weeks => (
                  <option key={weeks} value={weeks}>{weeks}주</option>
                ))}
              </select>
            </div>

            {propertyId && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>일정 관리</label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px', marginTop: '5px' }}>
                  달력에서 계약 가능한 날짜를 설정하세요.
                </p>
                <button
                  type="button"
                  onClick={() => setShowCalendarModal(true)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#6b46f2';
                    e.currentTarget.style.color = '#6b46f2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#ddd';
                    e.currentTarget.style.color = '#000';
                  }}
                >
                  일정 관리 달력 열기
                </button>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0 }}>{propertyId ? '방 수정하기' : '방 등록하기'}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
          padding: '0 20px'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '15px 20px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '3px solid #6b46f2' : '3px solid transparent',
                color: activeTab === tab.id ? '#6b46f2' : '#666',
                fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                transition: 'all 0.2s'
              }}
            >
              {tab.number} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0'
        }}>
          {renderTabContent()}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              background: '#000',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {propertyId ? '수정하기' : '등록하기'}
          </button>
        </div>
      </div>

      {showAddressSearch && (
        <AddressSearch
          onClose={() => setShowAddressSearch(false)}
          onSelectAddress={handleSelectAddress}
        />
      )}

      {showCalendarModal && propertyId && (
        <CalendarModal
          propertyId={propertyId}
          onClose={() => setShowCalendarModal(false)}
        />
      )}

      {/* 기본 옵션 선택 모달 */}
      {showBasicOptionsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>기본 옵션</h3>
              <button
                onClick={() => setShowBasicOptionsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Options Grid */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {['냉장고', '세탁기', '에어컨', 'TV', '와이파이', '싱크대', '가스레인지', '인덕션', '침대'].map(option => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      if (tempBasicOptions.includes(option)) {
                        setTempBasicOptions(tempBasicOptions.filter(o => o !== option));
                      } else {
                        setTempBasicOptions([...tempBasicOptions, option]);
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      border: tempBasicOptions.includes(option) ? '2px solid #000' : '1px solid #ddd',
                      borderRadius: '8px',
                      background: tempBasicOptions.includes(option) ? '#f0f0f0' : 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: tempBasicOptions.includes(option) ? 'bold' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span>{option}</span>
                    {tempBasicOptions.includes(option) && <span>✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: '10px'
            }}>
              <button
                onClick={() => setShowBasicOptionsModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 'bold'
                }}
              >
                닫기
              </button>
              <button
                onClick={() => {
                  handleInputChange('basic_options', tempBasicOptions);
                  setShowBasicOptionsModal(false);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#000',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 'bold'
                }}
              >
                선택하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 추가 옵션 선택 모달 */}
      {showAdditionalOptionsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>추가 옵션</h3>
              <button
                onClick={() => setShowAdditionalOptionsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Options Grid */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {[
                  '도어락', 'CCTV', '관리실', '식탁', '전자레인지', '전기밥솥',
                  '정수기', '소파', '책상', '옷장', '신발장', '화장대',
                  '욕조', '비데', '건조기', '발코니/베란다', '드레스룸', '케이블 TV',
                  '커튼', '공기청정기'
                ].map(option => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      if (tempAdditionalOptions.includes(option)) {
                        setTempAdditionalOptions(tempAdditionalOptions.filter(o => o !== option));
                      } else {
                        setTempAdditionalOptions([...tempAdditionalOptions, option]);
                      }
                    }}
                    style={{
                      padding: '12px 16px',
                      border: tempAdditionalOptions.includes(option) ? '2px solid #000' : '1px solid #ddd',
                      borderRadius: '8px',
                      background: tempAdditionalOptions.includes(option) ? '#f0f0f0' : 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: tempAdditionalOptions.includes(option) ? 'bold' : 'normal',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span>{option}</span>
                    {tempAdditionalOptions.includes(option) && <span>✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: '10px'
            }}>
              <button
                onClick={() => setShowAdditionalOptionsModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 'bold'
                }}
              >
                닫기
              </button>
              <button
                onClick={() => {
                  handleInputChange('additional_options', tempAdditionalOptions);
                  setShowAdditionalOptionsModal(false);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#000',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 'bold'
                }}
              >
                선택하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px',
  border: '1px solid #ddd',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

export default PropertyRegistration;
